<template>
  <div class="order-by-date-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Order By Date"
        :start="start"
        :end="end"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Order Details</div>
              <div class="report-subtitle">
                {{ Orders.length }} orders found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Total</div>
              <div class="summary-value">{{ total | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <v-data-table
              v-else
              dense
              :sort-by="sortBy"
              :mobile-breakpoint="0"
              :headers="headers"
              :items-per-page="-1"
              hide-default-footer
              :items="Orders"
              class="order-table"
            >
              <template v-slot:item.date="{ item }">
                {{ item.date | moment("MM/DD/YYYY") }}
              </template>

              <template v-slot:item.localId="{ item }">
                <a href="#" class="order-link" @click.prevent="viewOrder(item)">
                  {{ item.localId }}
                </a>

                <v-chip
                  v-if="item.completeDate"
                  small
                  color="green lighten-4"
                  text-color="green darken-4"
                  class="ml-2"
                >
                  Completed
                </v-chip>
              </template>

              <template v-slot:item.customerName="{ item }">
                <span class="text-capitalize">
                  {{ getCustomerName(item) }}
                </span>
              </template>

              <template v-slot:item.total="{ item }">
                <span class="money">
                  {{ item.total | currency }}
                </span>
              </template>

              <template v-slot:body.append v-if="Orders.length > 0">
                <tr class="total-row">
                  <td colspan="3" class="text-right font-weight-bold">
                    Total
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ total | currency }}
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  No orders found for this date range.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import orderService from "@/modules/Order/service.js";
import { mapGetters, mapActions } from "vuex";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "order-by-date-report",

  components: {
    ReportViewBar,
  },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Orders: [],
      sortBy: "localId",
      exportFields: {
        Date: "date",
        "Order #": "localId",
        Customer: "customerName",
        Status: "status",
        Amount: "total",
      },
      headers: [
        {
          text: "Created At",
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Order #",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Customer",
          align: "left",
          value: "customerName",
          sortable: true,
        },
        {
          text: "Amount",
          align: "right",
          value: "total",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters("global", ["currentBusiness"]),

    total() {
      return this.Orders.reduce((sum, row) => sum + Number(row.total || 0), 0);
    },

    exportData() {
      return this.Orders.map((row) => ({
        date: row.date,
        localId: row.localId,
        customerName: this.getCustomerName(row),
        status: row.completeDate ? "Completed" : "Open",
        total: Number(row.total || 0),
      }));
    },
  },

  created() {
    this.start = this.$route.query.start || null;
    this.end = this.$route.query.end || null;

    this.loadData();

    if (!this.currentBusiness) {
      this.initUser();
      this.initBusiness();
    }
  },

  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),

    getCustomerName(order) {
      if (order.Customer && order.Customer.name) return order.Customer.name;
      if (order.metadata && order.metadata.name) return order.metadata.name;

      return "N/A";
    },

    viewOrder(order) {
      const hashId = hashids.encode(order.id);

      const routeData = this.$router.resolve({
        path: `/global/order/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850,height=1100");
    },

    async loadData() {
      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;

      try {
        const response = await orderService.getAll(where);

        this.Orders = response.data || [];
        document.title = "Report - Order By Date";
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.order-by-date-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.order-link {
  font-weight: 700;
  text-decoration: none;
}

.money {
  font-weight: 700;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.order-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.order-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .order-by-date-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>